import { useState, useCallback } from "react";
import { useGetOne, useNotify, useRefresh, useRecordContext } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft,
  FileText,
  Calendar,
  Send,
  Loader2,
  ExternalLink,
  DollarSign,
  Shield,
} from "lucide-react";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";

interface Invoice {
  id: string;
  org_id: string;
  job_id: string | null;
  contact_id: string | number;
  invoice_number: string;
  invoice_type: string;
  status: string;
  title: string;
  description: string;
  line_items_json: Array<{
    description: string;
    quantity: number;
    unit_price: number;
    amount: number;
  }>;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  platform_fee_pct: number;
  platform_fee_fixed: number;
  platform_fee_amount: number;
  stripe_account_id: string | null;
  stripe_checkout_url: string | null;
  stripe_payment_intent_id: string | null;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
}

interface InvoiceDetailProps {
  invoiceId: string;
  onBack: () => void;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";

const TYPE_LABELS: Record<string, string> = {
  deposit: "Deposit",
  progress: "Progress Payment",
  final: "Final Payment",
  standard: "Standard",
};

export function InvoiceDetail({ invoiceId, onBack }: InvoiceDetailProps) {
  const { data: invoice, isPending } = useGetOne<Invoice>("invoices", {
    id: invoiceId,
  });
  const notify = useNotify();
  const refresh = useRefresh();
  const [sending, setSending] = useState(false);

  const handleSendInvoice = useCallback(async () => {
    if (!invoice) return;
    setSending(true);
    try {
      const { createClient } = await import(
        "https://esm.sh/@supabase/supabase-js@2"
      );
      const sb = createClient(
        SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY || ""
      );
      const {
        data: { session },
      } = await sb.auth.getSession();

      if (!session?.access_token) {
        notify("Please log in again", { type: "error" });
        return;
      }

      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/stripe-create-invoice`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ invoice_id: invoice.id }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");

      notify(
        `Invoice ${invoice.invoice_number} sent — checkout link created`,
        { type: "success" }
      );
      refresh();
    } catch (err) {
      notify(`Failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setSending(false);
    }
  }, [invoice, notify, refresh]);

  if (isPending || !invoice) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Loading invoice…
      </div>
    );
  }

  const canSend = invoice.status === "draft";
  const hasPaid = invoice.status === "paid";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-1.5 rounded hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-mono text-muted-foreground">
              {invoice.invoice_number}
            </span>
            <InvoiceStatusBadge status={invoice.status} />
            <span className="text-xs text-muted-foreground px-1.5 py-0.5 bg-gray-100 rounded">
              {TYPE_LABELS[invoice.invoice_type] || invoice.invoice_type}
            </span>
          </div>
          <h3 className="text-lg font-semibold truncate mt-0.5">
            {invoice.title || "Untitled Invoice"}
          </h3>
        </div>

        <div className="flex gap-2 shrink-0">
          {canSend && (
            <button
              onClick={handleSendInvoice}
              disabled={sending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              Send & Create Payment
            </button>
          )}
          {invoice.stripe_checkout_url && invoice.status === "sent" && (
            <a
              href={invoice.stripe_checkout_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border text-blue-600 hover:bg-blue-50 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Checkout Link
            </a>
          )}
        </div>
      </div>

      {/* Description */}
      {invoice.description && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm">{invoice.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Metadata */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          Created {formatDate(invoice.created_at)}
        </span>
        {invoice.sent_at && (
          <span>Sent {formatDate(invoice.sent_at)}</span>
        )}
        {invoice.paid_at && (
          <span className="text-green-600 font-medium">
            Paid {formatDate(invoice.paid_at)}
          </span>
        )}
      </div>

      {/* Line items */}
      {invoice.line_items_json.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b bg-gray-50">
              <h4 className="text-sm font-semibold">Line Items</h4>
            </div>
            <div className="divide-y">
              {invoice.line_items_json.map((item, i) => (
                <div key={i} className="px-4 py-3 flex justify-between">
                  <div>
                    <p className="text-sm font-medium">{item.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.quantity} × {fmt(item.unit_price)}
                    </p>
                  </div>
                  <p className="text-sm font-semibold tabular-nums">
                    {fmt(item.amount)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Totals */}
      <Card>
        <CardContent className="p-4">
          <div className="space-y-1.5 max-w-xs ml-auto">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="tabular-nums">{fmt(Number(invoice.subtotal))}</span>
            </div>
            {Number(invoice.tax_amount) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Tax ({(Number(invoice.tax_rate) * 100).toFixed(1)}%)
                </span>
                <span className="tabular-nums">
                  {fmt(Number(invoice.tax_amount))}
                </span>
              </div>
            )}
            <div className="border-t pt-1.5 flex justify-between text-lg font-bold">
              <span>Total</span>
              <span className="tabular-nums">{fmt(Number(invoice.total))}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform fee info (admin visibility only) */}
      {Number(invoice.platform_fee_amount) > 0 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Shield className="w-3.5 h-3.5" />
          Platform fee: {fmt(Number(invoice.platform_fee_amount))}
          ({(Number(invoice.platform_fee_pct) * 100).toFixed(1)}% + {fmt(Number(invoice.platform_fee_fixed))})
          {invoice.stripe_account_id && (
            <span className="font-mono">→ {invoice.stripe_account_id}</span>
          )}
        </div>
      )}

      {/* Payment confirmation */}
      {hasPaid && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4 flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm font-semibold text-green-800">
                Payment Received
              </p>
              <p className="text-xs text-green-700">
                {fmt(Number(invoice.total))} paid on{" "}
                {invoice.paid_at ? formatDate(invoice.paid_at) : "—"}
                {invoice.stripe_payment_intent_id && (
                  <span className="font-mono ml-1">
                    ({invoice.stripe_payment_intent_id})
                  </span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
